import { useEffect } from 'react';
import { DatePicker, Form, Input, Modal } from 'antd';
import dayjs from 'dayjs';
import useEmployeeNoticesT from '@/components/email-templates/useEmployeeNoticesT';
import { DATE_FORMATS } from '@/utils/formUtils';
import { itemLabel } from './equipmentLines';

const NOTE_MAX = 300;

/**
 * Date (today by default) and an optional note for one step of an item:
 * hand-over ("assign": serial / inventory note) or return ("return":
 * condition). `step` is { item, action } or null (closed).
 */
export default function EquipmentStepModal({ step, saving, onCancel, onSave }) {
  const { tn } = useEmployeeNoticesT();
  const [form] = Form.useForm();
  const isReturn = step?.action === 'return';

  useEffect(() => {
    if (step) form.setFieldsValue({ date: dayjs(), note: '' });
  }, [step, form]);

  const finish = (values) =>
    onSave({
      date: values.date.format(DATE_FORMATS.STANDARD),
      note: (values.note || '').trim(),
    });

  return (
    <Modal
      open={Boolean(step)}
      title={
        step
          ? tn(isReturn ? 'returnTitle' : 'assignTitle', {
              item: itemLabel(step.item),
            })
          : ''
      }
      okText={tn('save')}
      cancelText={tn('cancel')}
      confirmLoading={saving}
      okButtonProps={{ className: '!rounded-[50px]' }}
      cancelButtonProps={{ className: '!rounded-[50px]' }}
      onOk={() => form.submit()}
      onCancel={onCancel}
      destroyOnHidden
      width={440}
    >
      <Form form={form} layout="vertical" onFinish={finish} className="mt-3">
        <Form.Item
          name="date"
          label={tn('stepDate')}
          rules={[{ required: true, message: tn('stepDateRequired') }]}
        >
          <DatePicker
            className="w-full"
            format={DATE_FORMATS.DISPLAY}
            allowClear={false}
            disabledDate={(current) =>
              (current && current.isAfter(dayjs().add(31, 'day'), 'day')) ||
              (isReturn &&
                step?.item?.assignedAt &&
                current.isBefore(dayjs(step.item.assignedAt), 'day'))
            }
          />
        </Form.Item>
        <Form.Item
          name="note"
          label={tn(isReturn ? 'returnNote' : 'assignNote')}
        >
          <Input
            maxLength={NOTE_MAX}
            placeholder={tn(
              isReturn ? 'returnNotePlaceholder' : 'assignNotePlaceholder'
            )}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
